import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import {
  buildDiagnosticReportPayload,
  clearPendingCrash,
  getDiagnosticConsent,
  getPendingCrash,
  setDiagnosticConsent,
  type DiagnosticSubmitResult,
} from '../../lib/diagnostics';

export default function DiagnosticsCard() {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(() => getDiagnosticConsent());
  const [hasCrash, setHasCrash] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHasCrash(getPendingCrash() !== null);
  }, []);

  const toggleConsent = useCallback(() => {
    const next = !consent;
    setConsent(next);
    setDiagnosticConsent(next);
    setMessage(null);
    setError(null);
  }, [consent]);

  const submit = useCallback(async () => {
    if (!consent) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const payload = buildDiagnosticReportPayload(note);
      const res = await apiFetch<DiagnosticSubmitResult>('/v3/me/diagnostic-reports', {
        method: 'POST',
        body: payload,
        idempotencyKey: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `diag-${Date.now()}`,
      });
      clearPendingCrash();
      setHasCrash(false);
      setNote('');
      setMessage(res.data.deduplicated ? t('account.diagnosticsDeduped') : t('account.diagnosticsSent', { id: res.data.report_id }));
    } catch (e) {
      setError(e instanceof ApiError ? t('account.diagnosticsFailedMsg', { message: e.message }) : t('account.diagnosticsFailedRetry'));
    } finally {
      setBusy(false);
    }
  }, [consent, note, t]);

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.diagnostics')}</h2>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)' }}>
        <div style={{ minWidth: 0 }}>
          <div>{t('account.diagnosticsConsent')}</div>
          <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {consent ? t('account.diagnosticsDescOn') : t('account.diagnosticsDescOff')}
          </div>
        </div>
        <button
          role="switch"
          aria-checked={consent}
          aria-label={t('account.diagnosticsConsent')}
          disabled={busy}
          onClick={toggleConsent}
          style={{
            width: 44,
            height: 26,
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-surface-line)',
            background: consent ? 'var(--color-brand-primary)' : 'var(--color-surface-panel2)',
            position: 'relative',
            flexShrink: 0,
            opacity: busy ? 'var(--opacity-disabled)' : 1,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: consent ? 20 : 2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left var(--motion-duration-fast) var(--motion-easing-ease)',
            }}
          />
        </button>
      </div>
      <p className="text-secondary" style={{ margin: 'var(--spacing-4) 0 0', fontSize: 'var(--font-size-sm)' }}>
        {hasCrash ? t('account.diagnosticsPendingCrash') : t('account.diagnosticsNoPending')}
      </p>
      <label className="stack gap-2" style={{ marginTop: 'var(--spacing-4)' }}>
        <span>{t('account.diagnosticsNoteLabel')}</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
          disabled={busy}
          placeholder={t('account.diagnosticsNotePlaceholder')}
          style={{ minHeight: 86, paddingTop: 'var(--spacing-4)', resize: 'vertical' }}
        />
      </label>
      <div style={{ marginTop: 'var(--spacing-4)' }}>
        <button className="btn-primary" disabled={!consent || busy} onClick={() => void submit()}>
          {busy ? t('account.diagnosticsSending') : t('account.diagnosticsSend')}
        </button>
      </div>
      {message && (
        <p role="status" style={{ color: 'var(--color-semantic-success)', margin: 'var(--spacing-3) 0 0' }}>
          {message}
        </p>
      )}
      {error && (
        <p className="error-text" role="alert" style={{ margin: 'var(--spacing-3) 0 0' }}>
          {error}
        </p>
      )}
    </div>
  );
}
